import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Stylin' Strings: toggles header[data-ss-compact] while scrolling, used by the
 * shrinking logo and the collapsing mobile category bar (snippets/ss-header-styles.liquid).
 *
 * Uses separate thresholds for compacting and expanding so the header can't flicker
 * when the page rests near a single threshold.
 */

const COMPACT_AFTER = 80;
const EXPAND_BEFORE = 8;

/** @type {AbortController | null} */
let controller = null;

function init() {
  controller?.abort();
  controller = new AbortController();
  const { signal } = controller;

  const header = document.querySelector('header-component');
  if (!(header instanceof HTMLElement)) return;
  if (!header.hasAttribute('data-ss-logo-hero') && !header.hasAttribute('data-ss-hide-nav-on-scroll')) return;

  /** @type {number | null} */
  let rafId = null;

  const update = () => {
    rafId = null;
    const scrollTop = getScrollTop();
    const isCompact = header.hasAttribute('data-ss-compact');

    if (!isCompact && scrollTop > COMPACT_AFTER) {
      header.setAttribute('data-ss-compact', '');
    } else if (isCompact && scrollTop < EXPAND_BEFORE) {
      header.removeAttribute('data-ss-compact');
    }
  };

  const onScroll = () => {
    if (rafId === null) rafId = requestAnimationFrame(update);
  };

  // The scroll container changes between desktop and mobile layouts
  let scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', onScroll, { passive: true, signal });

  scrollContainerMediaQuery.addEventListener(
    'change',
    () => {
      scrollTarget.removeEventListener('scroll', onScroll);
      scrollTarget = getScrollEventTarget();
      scrollTarget.addEventListener('scroll', onScroll, { passive: true, signal });
      update();
    },
    { signal }
  );

  // Center the large logo across both rows: shift it down by half the bottom row's height
  const bottomRow = header.querySelector('.header__row--bottom');
  if (bottomRow instanceof HTMLElement) {
    const resizeObserver = new ResizeObserver(() => {
      header.style.setProperty('--ss-logo-shift', `${bottomRow.offsetHeight / 2}px`);
    });
    resizeObserver.observe(bottomRow);
    signal.addEventListener('abort', () => resizeObserver.disconnect());
  }

  update();
}

init();

// Re-initialize when the header is re-rendered in the theme editor
document.addEventListener('shopify:section:load', init);
